<template>
  <div v-if="readOnly" class="flex flex-col gap-x-2">
    <label v-if="label" :for="fieldName" class="">
      <span>{{ t9nLabel }}</span>
      <HintTippy v-if="hint" :hint="hint"></HintTippy>
    </label>
    <span class="text-base flex-1 ltr:font-sans">
      {{ lodashGet(formStore.draft?.request || formStore.form.values, fieldName, '--') }}
    </span>
  </div>
  <div v-else class="flex flex-col gap-y-1" :class="[fullWidth && '!w-full']">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
      <label v-if="label" :for="fieldName" :class="labelClass" class="flex items-center gap-x-2">
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col gap-y-1">
        <InputAppendage v-if="appendages?.top" v-bind="appendages">
          {{ $t(appendages?.top) }}
        </InputAppendage>
        <div class="w-full relative flex justify-center items-center">
          <component
            :is="lines > 1 ? 'textarea' : 'input'"
            :rows="lines"
            v-maska
            :data-maska="parsedMask?.mask"
            :data-maska-tokens="parsedMask?.tokens"
            :data-maska-reversed="parsedMask?.reversed"
            :id="fieldName"
            :name="fieldName"
            :value="inputValue"
            :readonly="isReadOnly"
            :disabled="withIsDisabled"
            :placeholder="t9nPlaceholder || t9nLabel"
            :type="showPassword ? (passwordVisible ? 'text' : 'password') : inputType"
            :style="{ width: inputWidth }"
            :class="[{ 'border-s-4 border-s-primary': isRequired }, textCapitalizationClass]"
            :maxlength="maxLength || maxLengthFromRules"
            class="ps-3 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary disabled:cursor-not-allowed read-only:bg-brand-gray-250 placeholder:normal-case"
            @input="handleInputChange"
            @blur="handleBlur"
          />
          <div
            v-if="props.showPassword"
            class="absolute end-2 cursor-pointer"
            @click="togglePasswordVisible"
          >
            <FontAwesomeIcon :icon="faEyeSlash" class="text-primary" />
          </div>
        </div>
        <InputAppendage
          v-if="Array.isArray(appendages?.bottom)"
          :font-weight="appendages.font_weight"
          v-bind="appendages"
        >
          <div v-for="(item, key) in appendages?.bottom" :key="key">
            {{ $t(item) }}
          </div>
        </InputAppendage>
        <InputAppendage
          v-else-if="appendages?.bottom"
          :font-weight="appendages.font_weight"
          v-bind="appendages"
        >
          {{ $t(appendages?.bottom) }}
        </InputAppendage>
      </div>
    </div>
    <div v-if="props.showPassword && props.showPasswordBar" class="flex gap-x-1">
      <div v-for="(item, key) in passwordValidatorList" :key="key">
        <div
          class="w-5 h-2 rounded-full"
          :class="[passwordText(item) ? 'bg-green-400' : 'bg-red-500']"
        ></div>
      </div>
    </div>
    <div v-if="hasError" class="w-full flex flex-col md:flex-row gap-x-4 gap-y-1 md:items-center">
      <div v-if="showErrorSpacer" class="w-full md:w-1/3"></div>
      <span class="w-full pt-[2px] text-brand-red ltr:font-sans text-xs">
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
import HintTippy from '@/components/Form/HintTyppy.vue'
import InputAppendage from '@/components/Form/InputAppendage.vue'
import useTextInputActions from '@/composables/actions/useTextInputActions.js'
import useRuleHandler from '@/composables/useRuleHandler'
import useVisible from '@/composables/useVisible'
import useWatchAction from '@/composables/watchActions/index'
import axios from '@/services'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { useDebounceFn } from '@vueuse/core'
import lodashGet from 'lodash/get'
import { vMaska } from 'maska'
import { useField } from 'vee-validate'
import { computed, onMounted, ref, toRef, useAttrs, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const attrs = useAttrs()

const props = defineProps({
  fullWidth: {
    type: Boolean,
    default: false
  },
  hint: String,
  showPassword: Boolean,
  showPasswordBar: Boolean,
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String
  },
  inputType: {
    type: String,
    default: () => 'text'
  },
  lines: {
    type: Number,
    default: () => 1
  },
  placeholder: String,
  isReadOnly: Boolean,
  isDisabled: {
    type: [Boolean, Object]
  },
  modelValue: {
    type: [String, Number]
  },
  defaultValue: String,
  maxLength: Number,
  rules: Array,
  showRules: Object,
  mask: String,
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  showErrorSpacer: {
    type: Boolean,
    default: () => true
  },
  trigger: String,
  onBlur: Object,
  dependentOn: String,
  debounceTime: {
    type: Number,
    default: () => 0
  },
  appendages: Object,
  readOnly: Boolean,
  onWatch: Object,
  inputWidth: {
    type: String,
    default: () => '100%'
  }
})

const route = useRoute()
const formStore = useFormStore()
const fieldName = toRef(props, 'fieldName')
const i18n = useI18n()
const passwordVisible = ref(false)
const passwordValidatorList = ref([/[A-Z]/, /[a-z]/, /[\d\W]/, /.{8,}/])
const { findShowRule, getBindObj } = useVisible()

const {
  value: inputValue,
  errorMessage,
  handleBlur: handleFieldBlur,
  handleChange,
  meta
} = useField(fieldName, (value) => useRuleHandler(value, props.rules), {
  initialValue: props.defaultValue
})

onMounted(async () => {
  if (props.trigger == 'load') {
    await useTextInputActions()[attrs.on_load.action]()
  }
})

const passwordText = (regex) => {
  return inputValue.value && regex.test(inputValue.value)
}

const produceActionParams = (params) => {
  let res = {}

  Object.keys(params).forEach((key) => {
    // condition 1: With ref option
    if (params[key]?.ref) {
      res[key] = formStore.form.values[params[key].ref]
    } else {
      // condition 2: string value
      res[key] = params[key]
    }
  })
  return res
}

/**
 * main handler for blur event.
 * if schema contains blur as its trigger,
 * check if it contains api.translate_to. if so, call api to translate
 * and pass the value to contains api.translate_to
 */
const handleBlur = async (event) => {
  handleFieldBlur()
  if (!event.target.value) return

  if (props.trigger == 'blur') {
    const { api } = props.onBlur
    if (api?.translate_to) {
      const response = await axios.request({
        method: api.method,
        url: api?.path.replaceAll('__reactive__', event.target.value)
      })

      formStore.form.setFieldValue(
        props.onBlur.api.translate_to,
        JSON.parse(response)[event.target.value.toLowerCase()]
      )

      return
    }

    if (props.onBlur.api?.on_success?.action) {
      const params = {}
      const payload = api?.query || api?.body

      if (Object.values(payload || {}).includes('__reactive__')) {
        const newQuery = JSON.parse(
          JSON.stringify(payload).replaceAll('__reactive__', event.target.value.replaceAll('-', ''))
        )
        Object.assign(params, newQuery)
      } else if (Object.values(params || {}).includes('__serviceTransactionId__')) {
        const newQuery = JSON.parse(
          JSON.stringify(params).replaceAll('__serviceTransactionId__', route.params.serviceId)
        )
        Object.assign(params, newQuery)
      } else {
        Object.assign(params, payload)
      }

      Object.entries(params).forEach(([key, val]) => {
        try {
          if ('ref' in val) {
            params[key] = formStore.form.values[val.ref]
          }
        } catch (error) {
          return
        }
      })

      const requestConfig = {
        method: api?.method,
        url: api?.path
      }

      if (api.method == 'GET') {
        requestConfig.params = params
      }

      if (api.method == 'POST') {
        requestConfig.data = params
      }

      let response
      try {
        response = await axios.request(requestConfig)
      } catch (error) {
        return
      }
      await useTextInputActions()[api.on_success.action](response)
    }

    if (props.onBlur.action) {
      if ('action_params' in props.onBlur) {
        let params = {}
        if (Object.values(props.onBlur.action_params || {}).includes('__reactive__')) {
          const newQuery = JSON.parse(
            JSON.stringify(props.onBlur.action_params).replaceAll(
              '__reactive__',
              event.target.value.replaceAll('-', '')
            )
          )
          Object.assign(params, newQuery)
        } else {
          params = props.onBlur.action_params
          params = produceActionParams(params)
        }
        return await useTextInputActions()[props.onBlur.action](params)
      }
      await useTextInputActions()[props.onBlur.action](props.onBlur?.action_params)
    }
  }
}

const t9nLabel = computed(() => i18n.t(props.label || ''))
const t9nPlaceholder = computed(() => props.placeholder && i18n.t(props.placeholder))
const textCapitalizationClass = computed(() => {
  switch (attrs.text_capitalization) {
    case 'characters':
      return 'uppercase'
    case 'words':
      return 'capitalize'
    default:
      return 'normal-case'
  }
})

const parsedMask = computed(() => {
  /**
   * create masks for certain rules, before applying any specific masks
   */
  if (props.rules?.some((r) => r.name == 'numberOnly')) {
    return {
      mask: '999#',
      tokens: '9:[0-9]:repeated',
      reversed: true
    }
  }

  if (!props.mask) return null

  if (props.mask == 'currency') {
    return {
      mask: '9,99#.##',
      tokens: '9:[0-9]:repeated',
      reversed: true
    }
  }

  if (props.mask == 'currencyNoDecimal') {
    return {
      mask: '9,999,999',
      tokens: '9:[0-9]:repeated',
      reversed: true
    }
  }

  return {
    mask: props.mask,
    tokens: null
  }
})

const handleInputChange = useDebounceFn((e) => {
  // e.target.value = inputValueFilter([props.maxLength], e.target.value)

  /**
   * input filters to prevent certain characters from being input (like {}:">?<>")
   */
  const isEnglishOnly = props.rules?.some((r) => r.name == 'englishOnly')

  if (isEnglishOnly) {
    const allowedRegex = /^[a-zA-Z0-9 ]*$/
    const cleaned = e.target.value
      .split('')
      .filter((char) => allowedRegex.test(char))
      .join('')
    e.target.value = cleaned
  }

  handleChange(e)
}, props.debounceTime)

const hasError = computed(() => errorMessage.value && meta.touched)

const isRequired = computed(() => {
  const requiredReactive = props.rules?.find((r) => r.name == 'required_reactive')
  if (requiredReactive) {
    return isRequiredByReactive(requiredReactive)
  }
  return !!props.rules?.find((r) => ['required', 'required_ui'].includes(r.name))
})

const togglePasswordVisible = () => {
  passwordVisible.value = !passwordVisible.value
}

const withIsDisabled = computed(() => {
  if (typeof props.isDisabled === 'boolean') {
    return props.isDisabled
  } else if (props.isDisabled && Object.keys(props.isDisabled).length > 0) {
    const result = findShowRule(
      props.isDisabled.any || props.isDisabled.all,
      props.isDisabled.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )

    return result
  } else {
    return false
  }
})

const maxLengthFromRules = computed(() => {
  const maxChar = props.rules?.find((r) => r.name == 'max_char')
  if (maxChar) {
    return maxChar.value
  }

  return null
})

watchEffect(() => {
  getBindObj(route.params.serviceId)
})

if (props.watchAction || props.trigger == 'watch') {
  useWatchAction()[props.watchAction]()
}

/**
 * provides support for the required rule based on certain values
 * reason:
 * due to sharing forms, it's inevitable to have clashing logic on
 * some of the schema. this is a way to support both while still
 * enabling sharing between services.
 * {
 *     "name": "required_reactive",
 *     "value": {
 *         "operator": "in",
 *         "value": [
 *             33
 *         ],
 *         "ref": "serviceTransactionId"
 *     },
 *     "message": "INVALID_REQUIRED"
 * }
 */
const isRequiredByReactive = (rule) => {
  const { operator, value, ref } = rule.value

  let compareAgainst
  if (ref == 'serviceTransactionId') {
    compareAgainst = useGlobalStore().startedService.serviceTransactionId
  } else {
    compareAgainst = formStore.form.values[ref]
  }

  switch (operator) {
    case 'in':
      return value.includes(compareAgainst)
    case 'equal':
      return compareAgainst == value
    case 'not_in':
      return [undefined, ...value].includes(compareAgainst) === false
    default:
      return false
  }
}
</script>

<style lang="scss" scoped></style>
